import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, MapPin, ArrowRight, Clock, Phone, MessageCircle, XCircle, Send, Navigation, Loader2, Bot, ChevronDown, Sparkles, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { BookingChat } from "@/components/BookingChat";
import { SupportChatButton } from "@/components/SupportChat";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending_payment: "bg-amber-100 text-amber-700 border-amber-300",
  pending_satispay: "bg-red-100 text-red-700 border-red-300",
  confirmed: "bg-emerald-100 text-emerald-700 border-emerald-300",
  cancelled: "bg-red-100 text-red-700 border-red-300",
  completed: "bg-blue-100 text-blue-700 border-blue-300",
};

const CATEGORIES = [
  "medical", "dental", "mechanic", "locksmith", "hotel",
  "spa", "restaurant", "beauty", "fitness", "veterinary", 
  "hairdresser", "private_clinic", "pizzeria", "bar", "parking",
  "lawyer", "accountant", "notary", "architect", "engineer",
  "psychologist", "physiotherapist", "optician", "pharmacy",
  "tailor", "plumber", "electrician", "painter", "carpenter",
  "driving_school", "photographer", "florist", "bakery",
  "butcher", "ice_cream", "sushi", "kebab", "takeaway_pizza",
  "coworking", "laundry", "pet_grooming", "tattoo", "barber",
  "other"
];

export default function CustomerDashboard() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [nearbyBusinesses, setNearbyBusinesses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [loadingBiz, setLoadingBiz] = useState(false);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  
  // Chat State
  const [chatBooking, setChatBooking] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);

  // AI Assistant State
  const [showAI, setShowAI] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const [aiResults, setAiResults] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMessage, setAiMessage] = useState("");

  useEffect(() => {
    fetchBookings();
    fetchBusinesses();
    // Trigger reminder check
    axios.post(`${API}/reminders/check`).catch(() => {});
  }, []);

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${API}/bookings/customer`, { withCredentials: true });
      setBookings(res.data);
    } catch {
      // ignore
    } finally {
      setLoadingBookings(false);
    }
  };

  const fetchBusinesses = async (search, category) => {
    setLoadingBiz(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (category && category !== "all") params.append("category", category);
      const res = await axios.get(`${API}/businesses?${params.toString()}`);
      setBusinesses(res.data);
    } catch {
      // ignore
    } finally {
      setLoadingBiz(false);
    }
  };

  const handleSearch = () => {
    fetchBusinesses(searchQuery, categoryFilter);
  };

  const handleCategoryChange = (val) => {
    setCategoryFilter(val);
    fetchBusinesses(searchQuery, val);
  };

  const enableLocation = () => {
    if (navigator.geolocation) {
      setLoadingNearby(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lon: position.coords.longitude
          };
          setUserLocation(loc);
          fetchNearbyBusinesses(loc.lat, loc.lon);
        },
        (error) => {
          toast.error("Impossibile ottenere la posizione");
          setLoadingNearby(false);
        }
      );
    } else {
      toast.error("Geolocalizzazione non supportata");
    }
  };

  const fetchNearbyBusinesses = async (lat, lon, category = null) => {
    setLoadingNearby(true);
    try {
      const params = new URLSearchParams({ lat, lon, radius: 100 });
      if (category && category !== "all") params.append("category", category);
      const res = await axios.get(`${API}/businesses/nearby?${params.toString()}`);
      setNearbyBusinesses(res.data);
    } catch {
      toast.error("Errore nella ricerca");
    } finally {
      setLoadingNearby(false);
    }
  };

  const canCancel = (booking) => {
    if (booking.status === "cancelled" || booking.status === "completed") return false;
    try {
      const bookingDt = new Date(`${booking.date}T${booking.time_slot}:00Z`);
      const now = new Date();
      return (bookingDt - now) >= 3600000; // 1 hour in ms
    } catch {
      return false;
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm(t("cancel.confirm"))) return;
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`, { status: "cancelled" }, { withCredentials: true });
      toast.success(t("status.cancelled"));
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("cancel.too_late"));
    }
  };

  const handleAIRecommend = async () => {
    if (!aiInput.trim()) return;
    setAiLoading(true);
    setAiMessage("");
    setAiResults([]);
    try {
      const params = new URLSearchParams({ query: aiInput });
      if (categoryFilter && categoryFilter !== "all") params.append("category", categoryFilter);
      const res = await axios.get(`${API}/ai/recommend?${params.toString()}`, { withCredentials: true });
      if (res.data.recommendations && res.data.recommendations.length > 0) {
        setAiResults(res.data.recommendations);
      } else if (res.data.ai_response) {
        setAiMessage(res.data.ai_response);
      } else {
        setAiMessage("Non ho trovato suggerimenti. Prova con una ricerca diversa!");
      }
    } catch {
      setAiMessage("Mi dispiace, c'è stato un errore. Riprova!");
    } finally {
      setAiLoading(false);
    }
  };

  const _todayCD = new Date();
  const today = `${_todayCD.getFullYear()}-${String(_todayCD.getMonth()+1).padStart(2,"0")}-${String(_todayCD.getDate()).padStart(2,"0")}`;
  const upcoming = bookings.filter((b) => b.date >= today && b.status !== "cancelled");
  const past = bookings.filter((b) => b.date < today || b.status === "cancelled");

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <div className="pt-28 pb-16 px-6 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <h1
            className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-[#2A1B14] mb-12"
            data-testid="customer-dashboard-title"
          >
            {t("cust.dashboard.title")}
          </h1>

          <Tabs defaultValue="bookings" className="w-full">
            <TabsList className="rounded-full border border-[rgba(226,114,91,0.1)] bg-white/80 backdrop-blur-sm h-12 mb-8 p-1 shadow-warm">
              <TabsTrigger
                value="bookings"
                className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E2725B] data-[state=active]:to-[#F39C12] data-[state=active]:text-white data-[state=active]:shadow-md px-6 transition-all duration-200"
                data-testid="tab-bookings"
              >
                {t("biz.bookings")}
              </TabsTrigger>
              <TabsTrigger
                value="browse"
                className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E2725B] data-[state=active]:to-[#F39C12] data-[state=active]:text-white data-[state=active]:shadow-md px-6 transition-all duration-200"
                data-testid="tab-browse"
              >
                {t("cust.browse")}
              </TabsTrigger>
            </TabsList>

            {/* Bookings Tab */}
            <TabsContent value="bookings">
              {loadingBookings ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin h-8 w-8 border-2 border-[#E2725B] border-t-transparent rounded-full" />
                </div>
              ) : bookings.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl border border-[rgba(226,114,91,0.1)] shadow-warm">
                  <p className="text-[#6A564A] text-sm">{t("cust.no_bookings")}</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {upcoming.length > 0 && (
                    <div>
                      <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-4">
                        {t("cust.upcoming")}
                        <span className="ml-2 text-[10px] text-slate-400 normal-case tracking-normal">({t("cancel.policy")})</span>
                      </h3>
                      <div className="space-y-3">
                        {upcoming.map((b) => (
                          <div
                            key={b.booking_id}
                            className="p-6 bg-white rounded-2xl border border-[rgba(226,114,91,0.1)] shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                            data-testid={`booking-${b.booking_id}`}
                          >
                            <div>
                              <p className="font-heading font-bold text-slate-800">{b.business_name}</p>
                              <p className="text-sm text-slate-500 mt-1">
                                <Clock className="inline h-3.5 w-3.5 mr-1" />
                                {b.date} - {b.time_slot}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={`rounded-full border text-xs px-3 py-1 ${STATUS_COLORS[b.status] || ""}`}>
                                {t(`status.${b.status}`)}
                              </Badge>
                              {b.order_status === "preparing" && (
                                <Badge className="rounded-full bg-blue-100 text-blue-700 border-blue-300 text-xs px-3 py-1 animate-pulse" data-testid={`order-preparing-${b.booking_id}`}>
                                  In Lavorazione
                                </Badge>
                              )}
                              {b.order_status === "ready" && (
                                <Badge className="rounded-full bg-emerald-100 text-emerald-700 border-emerald-300 text-xs px-3 py-1" data-testid={`order-ready-${b.booking_id}`}>
                                  Pronto! Parti da casa
                                </Badge>
                              )}
                              <button
                                onClick={() => {
                                  setChatBooking(b);
                                  setChatOpen(true);
                                }}
                                className="p-1.5 hover:bg-blue-50 transition-colors rounded-lg border border-slate-200"
                                data-testid={`chat-booking-${b.booking_id}`}
                              >
                                <Send className="h-4 w-4 text-blue-500" />
                              </button>
                              {canCancel(b) && (
                                <button
                                  onClick={() => handleCancel(b.booking_id)}
                                  className="p-1.5 hover:bg-red-50 transition-colors rounded-lg border border-red-200"
                                  data-testid={`cancel-booking-${b.booking_id}`}
                                >
                                  <XCircle className="h-4 w-4 text-red-500" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {past.length > 0 && (
                    <div>
                      <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-4">{t("cust.past")}</h3>
                      <div className="space-y-3">
                        {past.map((b) => (
                          <div
                            key={b.booking_id}
                            className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4 opacity-60"
                          >
                            <div>
                              <p className="font-heading font-bold text-slate-800">{b.business_name}</p>
                              <p className="text-sm text-slate-500 mt-1">
                                <Clock className="inline h-3.5 w-3.5 mr-1" />
                                {b.date} - {b.time_slot}
                              </p>
                            </div>
                            <Badge className={`rounded-full border text-xs px-3 py-1 ${STATUS_COLORS[b.status] || ""}`}>
                              {t(`status.${b.status}`)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Browse Tab */}
            <TabsContent value="browse">
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    data-testid="search-businesses-input"
                    placeholder={t("cust.search")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="rounded-xl pl-10 border-orange-200 h-10"
                  />
                </div>
                <Select value={categoryFilter} onValueChange={handleCategoryChange}>
                  <SelectTrigger className="rounded-xl border-orange-200 w-full sm:w-[180px] h-10 bg-white" data-testid="category-filter">
                    <SelectValue placeholder={t("cat.all")} />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl bg-white">
                    <SelectItem value="all" className="rounded-lg">{t("cat.all")}</SelectItem>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat} className="rounded-lg">{t(`cat.${cat}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  data-testid="search-button"
                  onClick={handleSearch}
                  className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:shadow-lg hover:shadow-orange-200 border-0 h-10"
                >
                  {t("common.search")}
                </Button>
                <Button
                  data-testid="nearby-button"
                  onClick={enableLocation}
                  disabled={loadingNearby}
                  className="rounded-xl bg-gradient-to-r from-sky-500 to-blue-500 text-white hover:shadow-lg hover:shadow-sky-200 border-0 h-10"
                >
                  {loadingNearby ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Navigation className="h-4 w-4 mr-2" />
                      {t("geo.nearby")}
                    </>
                  )}
                </Button>
              </div>

              {/* Nearby Results - Distance Bands (Satispay style) */}
              {nearbyBusinesses.length > 0 && (
                <div className="mb-8 space-y-4" data-testid="nearby-results">
                  <h3 className="text-xs uppercase tracking-widest text-sky-700 mb-2 flex items-center gap-2">
                    <Navigation className="h-4 w-4" />
                    {t("geo.nearby")} ({nearbyBusinesses.length} trovati)
                  </h3>
                  {/* Group by proximity band */}
                  {[
                    { band: "immediate", label: "Vicino a te", color: "emerald", icon: "pin", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-800" },
                    { band: "nearby", label: "Nella tua zona", color: "sky", icon: "area", bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", badge: "bg-sky-100 text-sky-800" },
                    { band: "area", label: "In provincia", color: "amber", icon: "prov", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-800" },
                    { band: "region", label: "Nella regione", color: "purple", icon: "reg", bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700", badge: "bg-purple-100 text-purple-800" },
                  ].map(({ band, label, bg, border, text, badge }) => {
                    const bandBiz = nearbyBusinesses.filter((b) => b.proximity_band === band);
                    if (bandBiz.length === 0) return null;
                    return (
                      <div key={band} className={`rounded-2xl ${border} border overflow-hidden`} data-testid={`band-${band}`}>
                        <div className={`${bg} px-4 py-2.5 flex items-center justify-between`}>
                          <span className={`text-xs uppercase tracking-widest font-bold ${text}`}>
                            {label}
                          </span>
                          <Badge className={`rounded-full text-[10px] px-2 py-0.5 ${badge} border-0`}>
                            {bandBiz.length}
                          </Badge>
                        </div>
                        {bandBiz.map((biz, i) => (
                          <div
                            key={biz.business_id}
                            className={`p-4 flex items-center justify-between gap-4 hover:${bg} transition-colors cursor-pointer bg-white ${i < bandBiz.length - 1 ? `border-b ${border}` : ""}`}
                            onClick={() => navigate(`/book/${biz.business_id}`)}
                            data-testid={`nearby-biz-${biz.business_id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-slate-800 truncate">{biz.name}</span>
                                {biz.is_featured && (
                                  <Badge className="rounded-full text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 border-amber-300">
                                    In Evidenza
                                  </Badge>
                                )}
                                <Badge className="rounded-full text-[10px] px-2 py-0.5 bg-slate-100 text-slate-600 border-slate-200">
                                  {t(`cat.${biz.category}`)}
                                </Badge>
                              </div>
                              <p className="text-xs text-slate-500 mt-1 truncate">
                                <MapPin className="inline h-3 w-3 mr-1" />
                                {biz.address || "Indirizzo non specificato"}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <span className={`text-lg font-bold ${text}`}>{biz.distance_km} km</span>
                              <ArrowRight className={`h-4 w-4 ${text} ml-1 inline`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}

              {loadingBiz ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin h-8 w-8 border-2 border-[#E2725B] border-t-transparent rounded-full" />
                </div>
              ) : businesses.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl border border-[rgba(226,114,91,0.1)] shadow-warm">
                  <p className="text-[#6A564A] text-sm">{t("common.no_results")}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {businesses.map((biz) => (
                    <div
                      key={biz.business_id}
                      className="group bg-white rounded-3xl border border-[rgba(226,114,91,0.08)] shadow-warm overflow-hidden hover:shadow-warm-lg hover:-translate-y-2 transition-all duration-300 cursor-pointer"
                      onClick={() => navigate(`/book/${biz.business_id}`)}
                      data-testid={`business-card-${biz.business_id}`}
                    >
                      <div className="h-3 bg-gradient-to-r from-[#E2725B] to-[#F39C12] group-hover:h-4 transition-all duration-300" />
                      <div className="p-6 lg:p-8">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge className="rounded-full border border-[rgba(226,114,91,0.2)] bg-[#E2725B]/5 text-[#E2725B] text-[10px] px-3 py-1 font-bold">
                            {t(`cat.${biz.category}`)}
                          </Badge>
                        </div>
                        <h3 className="font-heading font-bold text-lg text-[#2A1B14] mb-2 group-hover:text-[#E2725B] transition-colors">
                          {biz.name}
                        </h3>
                        {biz.address && (
                          <p className="text-xs text-[#6A564A] mt-1 flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-[#E2725B]" strokeWidth={1.5} />
                            {biz.address}
                          </p>
                        )}
                        {biz.phone && (
                          <p className="text-xs text-[#6A564A] mt-0.5 flex items-center gap-1">
                            <Phone className="h-3 w-3 text-[#E2725B]" strokeWidth={1.5} />
                            {biz.phone}
                          </p>
                        )}
                        {biz.whatsapp && (
                          <a
                            href={`https://wa.me/${biz.whatsapp.replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1 bg-[#25D366] text-white rounded-full text-xs font-bold hover:bg-[#128C7E] transition-colors"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`whatsapp-${biz.business_id}`}
                          >
                            <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            WhatsApp
                          </a>
                        )}
                        <div className="mt-5 pt-4 border-t border-[rgba(226,114,91,0.08)] flex items-center justify-between">
                          <span className="text-xs text-[#6A564A]">
                            {["lawyer","accountant","notary","architect","engineer"].includes(biz.category)
                              ? <span className="text-emerald-600 font-semibold">Prenotazione Gratuita</span>
                              : ["medical","dental","private_clinic","psychologist","physiotherapist","optician","veterinary"].includes(biz.category)
                                ? "Commissione: 1.00€"
                                : `${t("book.deposit")}: ${(biz.deposit_amount || 5).toFixed(2)}€`
                            }
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-[#E2725B] group-hover:translate-x-1 transition-transform">
                            {t("common.book_now")}
                            <ArrowRight className="h-3 w-3" />
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Booking Chat Dialog */}
      <BookingChat
        booking={chatBooking}
        isOpen={chatOpen}
        onClose={() => {
          setChatOpen(false);
          setChatBooking(null);
        }}
        currentUserType="customer"
      />

      {/* AI Recommendation Floating Widget */}
      <AnimatePresence>
        {showAI && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-6 w-[360px] max-w-[calc(100vw-48px)] bg-white rounded-3xl shadow-2xl border border-[rgba(226,114,91,0.15)] overflow-hidden z-50"
            data-testid="ai-recommend-panel"
          >
            <div className="bg-gradient-to-r from-[#E2725B] to-[#F39C12] px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm">{t("ai.recommend")}</h3>
                  <p className="text-[10px] text-white/70">{t("ai.recommend.desc")}</p>
                </div>
              </div>
              <button onClick={() => setShowAI(false)} className="text-white/70 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 max-h-[350px] overflow-y-auto">
              {aiResults.length === 0 && !aiMessage && !aiLoading && (
                <div className="text-center py-6">
                  <Sparkles className="h-8 w-8 mx-auto text-[#E2725B]/30 mb-2" />
                  <p className="text-sm text-slate-400">Chiedimi cosa cerchi! Es: "Voglio una pizza buona vicino a me"</p>
                </div>
              )}
              {aiMessage && (
                <div className="bg-gray-50 rounded-2xl px-4 py-3 text-sm text-slate-700 mb-3">
                  {aiMessage}
                </div>
              )}
              {aiResults.map((rec, i) => (
                <div
                  key={i}
                  className="p-3 bg-gradient-to-r from-[#E2725B]/5 to-[#F39C12]/5 rounded-xl border border-[rgba(226,114,91,0.1)] mb-2 cursor-pointer hover:shadow-md transition-all"
                  onClick={() => {
                    if (rec.business_id) navigate(`/book/${rec.business_id}`);
                  }}
                  data-testid={`ai-rec-${i}`}
                >
                  <p className="font-bold text-sm text-[#2A1B14]">{rec.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{rec.reason}</p>
                </div>
              ))}
              {aiLoading && (
                <div className="flex items-center gap-2 py-4 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin text-[#E2725B]" />
                  <span className="text-sm text-slate-400">{t("ai.assistant.thinking")}</span>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 p-3 flex gap-2">
              <input
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAIRecommend()}
                placeholder="Cosa stai cercando?"
                className="flex-1 text-sm px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-[#E2725B] focus:ring-1 focus:ring-[#E2725B]/20"
                data-testid="ai-recommend-input"
              />
              <button
                onClick={handleAIRecommend}
                disabled={aiLoading || !aiInput.trim()}
                className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#E2725B] to-[#F39C12] flex items-center justify-center text-white disabled:opacity-50 hover:shadow-lg transition-all"
                data-testid="ai-recommend-send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating AI Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setShowAI(!showAI)}
        className={`fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-xl flex items-center justify-center z-50 transition-colors ${
          showAI
            ? "bg-slate-700 shadow-slate-300"
            : "bg-gradient-to-r from-[#E2725B] to-[#F39C12] shadow-[#E2725B]/30"
        }`}
        data-testid="ai-recommend-toggle"
      >
        {showAI ? (
          <ChevronDown className="h-6 w-6 text-white" />
        ) : (
          <Bot className="h-6 w-6 text-white" />
        )}
      </motion.button>
      <SupportChatButton />
    </div>
  );
}
